import { ChatBot, NextPage, NextPageHead, OfficeLocationContextProvider, PageThemeContextProvider, PageView } from '@aspendental/shared-components-web';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import dynamic from 'next/dynamic';
import { Logger } from 'pino';
import { getBffCitiesByBrand, getBffStatesByBrand } from '../../services/bffFacilityCalls';
import { callsByBrand } from '../../services/callsByBrand';
import { capitalizeFirstCharacter } from '../utils/capitalize-first-character/';
import choosePreview from '../utils/choose-preview';
import DetermineIndexing from '../utils/determine-indexing';
import { getServerSideTranslations } from '../utils/get-serverside-translations';
import { NOT_FOUND } from '@src/constants';
import { AppMetadataByAppNameDocument, AppMetadataByAppNameQuery, AppMetadataByAppNameQueryVariables, AppPagesDataByAppNameDocument, AppPagesDataByAppNameQuery, AppPagesDataByAppNameQueryVariables, LandingPageByAppNameWithPathDocument, LandingPageByAppNameWithPathQuery, LandingPageByAppNameWithPathQueryVariables } from '@src/lib/__generated/sdk';
import { getBeffeGraphQlUrl, getEnrichedPageContents, getRootLevelConfig, getUserCoordinates, setCacheHeader } from '@src/pages/utils/';
import getConfigServices from '@src/pages/utils/get-config-services';
import { getDeviceTypeFromHeaders } from '@src/pages/utils/get-device-type-from-headers';
const UpdateLivePreviewData = dynamic(() => import('@src/components/shared/providers/ContentfulLivePreview').then(mod => mod.UpdateLivePreviewData));
const Home = (props: Readonly<InferGetServerSidePropsType<typeof getServerSideProps>>) => {
  if (props.preview) {
    return <UpdateLivePreviewData InternalComponent={InternalHome} data={props} />;
  }
  return <InternalHome {...props} data-sentry-element="InternalHome" data-sentry-component="Home" data-sentry-source-file="index.page.tsx" />;
};
const InternalHome = (props: Readonly<InferGetServerSidePropsType<typeof getServerSideProps>>) => {
  const {
    appData,
    config,
    states,
    cities,
    breadcrumbsData,
    page
  } = props;
  const {
    url: logoUrl
  } = appData?.logo || {};
  const {
    path,
    title,
    indexed,
    description,
    canonical,
    disableChatBot = false,
    analyticsContext,
    ogImage
  } = page;
  const pageTheme = page?.pageTheme || 'Primary';
  const {
    features,
    featureFlags,
    siteURL,
    logo
  } = config as BrandThemeConfig;
  return <PageThemeContextProvider pageTheme={pageTheme} data-sentry-element="PageThemeContextProvider" data-sentry-component="InternalHome" data-sentry-source-file="index.page.tsx">
			<OfficeLocationContextProvider states={states} cities={cities} data-sentry-element="OfficeLocationContextProvider" data-sentry-source-file="index.page.tsx">
				<PageView analyticsContext={analyticsContext} siteSection="Office Details" data-sentry-element="PageView" data-sentry-source-file="index.page.tsx" />
				<NextPageHead path={path} title={title} indexed={DetermineIndexing(indexed, props.env, props.preview, featureFlags.doNotIndexWholeSite)} description={description} canonical={canonical} breadcrumbTitle={breadcrumbsData?.breadcrumbTitle} breadcrumbs={breadcrumbsData?.breadcrumbsCollection.items} originUrl={siteURL} ogImage={ogImage?.desktopImage?.image?.cdn_url ?? logo.dark} data-sentry-element="NextPageHead" data-sentry-source-file="index.page.tsx" />
				<NextPage pageData={{
        ...page
      }} data-sentry-element="NextPage" data-sentry-source-file="index.page.tsx" />
				{!disableChatBot && <ChatBot url={features.chat.chatBotURL} isIconButton={features.chat.isIconButton} logoUrl={logoUrl} featureList={featureFlags.featureList} />}
			</OfficeLocationContextProvider>
		</PageThemeContextProvider>;
};
export const getServerSideProps: GetServerSideProps = async ({
  locale,
  req,
  res,
  preview = false
}) => {
  preview = choosePreview(req.headers.host, preview);
  // logger name is home because of backwards compatibility
  const {
    logger,
    brandConfig,
    gqlClient,
    appName
  } = getRootLevelConfig({
    req,
    preview,
    loggerName: 'home'
  });
  const facilityDetailsBasePath = brandConfig.features.otherApps.facilityDetailsBasePath;
  const deviceType = getDeviceTypeFromHeaders(req.headers);
  try {
    const [appPagesData, appMetadata, pageData, translations] = await Promise.all([gqlClient.query<AppPagesDataByAppNameQuery, AppPagesDataByAppNameQueryVariables>(AppPagesDataByAppNameDocument, {
      appName,
      preview
    }).toPromise(), gqlClient.query<AppMetadataByAppNameQuery, AppMetadataByAppNameQueryVariables>(AppMetadataByAppNameDocument, {
      appName,
      preview
    }).toPromise(), gqlClient.query<LandingPageByAppNameWithPathQuery, LandingPageByAppNameWithPathQueryVariables>(LandingPageByAppNameWithPathDocument, {
      appName,
      path: `/${facilityDetailsBasePath}/`,
      preview
    }).toPromise(), getServerSideTranslations(locale)]);
    if (appPagesData.error) {
      logger.warn(`CONTENTFUL_WARN_DIRECTORY_PAGE: ${appPagesData.error}`);
    }
    if (appMetadata.error) {
      logger.error(`CONTENTFUL_ERROR: ${appMetadata.error}`);
    }
    logger.info(`Index cms appPagesData collection - ${JSON.stringify(appPagesData.data?.appCollection)}`);
    logger.info(`Index cms appMetadata collection - ${JSON.stringify(appMetadata.data?.appCollection)}`);
    const appData = {
      ...appPagesData.data?.appCollection?.items[0],
      ...appMetadata.data?.appCollection?.items[0]
    };
    // replace [env] with environment name for locationBarFindOfficeUrl
    // TODO: create a brand variable context to replace those variables from CMS in future
    if (appData && appData.header?.locationBarFindOfficeUrl) {
      const brandSubdomain = process.env.ENVIRONMENT_NAME !== 'prod' ? 'wwwstg' : 'www';
      appData.header.locationBarFindOfficeUrl = appData.header?.locationBarFindOfficeUrl?.replace('[env]', brandSubdomain);
    }
    if (!appData) {
      return NOT_FOUND;
    }
    if (pageData.error) {
      logger.error(`CONTENTFUL_ERROR: ${pageData.error}`);
    }
    logger.info(`Index cms pageData collection - ${JSON.stringify(pageData?.data?.pageCollection)}`);
    const page = pageData?.data?.pageCollection?.items[0];
    logger.info(`/${facilityDetailsBasePath}/`);
    if (!page) {
      logger.error(`COULD_NOT_FIND_THE_HOME_PAGE - /${facilityDetailsBasePath}/`);
      return NOT_FOUND;
    }
    const transformedServices = getConfigServices(req, brandConfig);
    const [[states, cities], enrichedPageContents] = await Promise.all([getStatesAndCities(transformedServices, appData, brandConfig, logger), getEnrichedPageContents({
      page,
      gqlClient,
      preview,
      logger
    })]);
    const breadcrumbsData = {
      breadcrumbTitle: capitalizeFirstCharacter(facilityDetailsBasePath),
      breadcrumbsCollection: {
        items: [{
          breadcrumbTitle: 'Home',
          path: '/'
        }]
      }
    };
    setCacheHeader(res, preview);
    return {
      props: {
        ...translations,
        preview,
        env: {
          ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME
        },
        config: {
          ...brandConfig,
          ...(appData.featureFlags ? {
            featureFlags: appData.featureFlags
          } : {}),
          services: transformedServices,
          schemaUrl: `${brandConfig.siteURL}${req.url}`
        },
        page: {
          ...page,
          contentsCollection: {
            items: enrichedPageContents
          }
        },
        appData,
        user: getUserCoordinates(req, logger),
        footerData: appData?.footer,
        breadcrumbsData,
        states,
        cities,
        deviceType
      }
    };
  } catch (e) {
    logger.error(`OFFICE_DIRECTORY_HOME_PAGE_LOAD_FAILURE: ${e}`);
    return NOT_FOUND;
  }
};
export default Home;
async function getStatesAndCities(transformedServices: ReturnType<typeof getConfigServices>, appData: any, brandConfig: BrandThemeConfig, logger: Logger) {
  const beffeGraphQlUrl = getBeffeGraphQlUrl(transformedServices);
  if (appData.featureFlags.useFacilityBeffeDataSource) {
    const [stateData, cityData] = await Promise.all([getBffStatesByBrand(brandConfig.bffBrandType, beffeGraphQlUrl), getBffCitiesByBrand(brandConfig.bffBrandType, beffeGraphQlUrl)]);
    logger.info(`Index Beffe states data - ${JSON.stringify(stateData.data?.facilityStates)}`);
    logger.info(`Index Beffe cities data - ${JSON.stringify(cityData.data?.facilityCities)}`);
    const states = stateData.data?.facilityStates || [];
    const cities = cityData?.data?.facilityCities?.map(city => ({
      city: city?.city,
      state: city?.state,
      stateAbbreviation: city?.stateAbbreviation,
      centers: city?.centers?.length ? city.centers.map(center => ({
        ...center,
        location: {
          street1: center?.address?.address1,
          zip: center?.address?.zipCode
        }
      })) : []
    })) || [];
    return [states, cities];
  } else {
    return callsByBrand[brandConfig.themeId].getAllCitiesAndStates();
  }
}